import { useEffect, useRef } from "react";
import { useReactFlow } from "@xyflow/react";

// Present-mode camera. Rendered *inside* <ReactFlow> (Viewport's top level is
// outside the RF context, so it can't call useReactFlow itself). On each step it
// frames the whole revealed set at a modest zoom; when a node is clicked it zooms
// in close. Null-rendering — it only drives fitView.

// Group framing: modest, so the whole revealed set stays legible (not a tight
// per-node zoom). Node framing: close, for the click-to-focus interaction.
export const GROUP_FIT = { padding: 0.28, maxZoom: 0.9, duration: 800 } as const;
export const NODE_FIT  = { padding: 0.4,  maxZoom: 1.4, duration: 600 } as const;

interface Props {
  isRevealMode: boolean;
  revealPointer: number;
  revealedIds: string[];
  focusNodeId: string | null;
}

export default function RevealCamera({ isRevealMode, revealPointer, revealedIds, focusNodeId }: Props) {
  const { fitView } = useReactFlow();

  // Read the latest ids without making them an effect dependency — the group fit
  // should fire on step change (and mount), not on every array-identity change.
  const idsRef = useRef(revealedIds);
  idsRef.current = revealedIds;

  // Group fit: on enter (mount) and each step change.
  useEffect(() => {
    if (!isRevealMode) return;
    const ids = idsRef.current;
    if (ids.length === 0) {
      void fitView(GROUP_FIT); // nothing revealed yet → frame the whole map
      return;
    }
    void fitView({ nodes: ids.map((id) => ({ id })), ...GROUP_FIT });
  }, [isRevealMode, revealPointer, fitView]);

  // Node fit: zoom close when a node is focused (click-to-focus).
  useEffect(() => {
    if (!isRevealMode || !focusNodeId) return;
    void fitView({ nodes: [{ id: focusNodeId }], ...NODE_FIT });
  }, [isRevealMode, focusNodeId, fitView]);

  return null;
}
